import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { EnumEstadoCiclo } from '../../../api/oData/enums/enumEstadoCiclo'
import { envConfig } from '../../../config/envConfig'
import useRetries from '../../../hooks/useRetries'
import { NonAuthRoutes } from '../../../model'
import {
  invoiceCycleStatusConfig,
  resolveNavigateTo
} from '../invoiceCyclesProcessing/invoiceCycleStatusConfig'

/**
 * Configuración del polling desde envConfig
 */
const POLLING_CONFIG = {
  delay: envConfig.invoiceCycleConfig.pollingDelay,
  maxRetries: envConfig.invoiceCycleConfig.pollingMaxRetries
}

export interface InvoiceCyclePollingState {
  isActive: boolean
  currentRetry: number
  isPaused: boolean
  maxRetries: number
}

export interface UseInvoiceCyclePollingResult {
  pollingState: InvoiceCyclePollingState
  resumePolling: () => void
  cancelPolling: () => void
  nextStatus: EnumEstadoCiclo | null
  navigateToNext: () => void
}

/**
 * Hook para gestionar el polling del ciclo de facturación
 *
 * Características:
 * - Polling con número limitado de intentos
 * - Delay configurable entre llamadas
 * - Pausado automático tras X intentos
 * - Capacidad de reanudar manualmente
 * - Navegación automática según cambio de estado (opcional)
 *
 * @param originState - Estado de origen desde donde se navegó (para evitar race conditions)
 * @param disableAutoNavigation - Si es true, no navega automáticamente, solo establece nextStatus
 */
export function useInvoiceCyclePolling(
  executionCycleId: number,
  currentStatus: EnumEstadoCiclo,
  fetchCycleStatus: () => Promise<EnumEstadoCiclo>,
  additionalNavigationState?: any,
  originState?: EnumEstadoCiclo,
  disableAutoNavigation: boolean = false
): UseInvoiceCyclePollingResult {
  const history = useHistory()
  const [pollingState, setPollingState] = useState<InvoiceCyclePollingState>({
    isActive: false,
    currentRetry: 0,
    isPaused: false,
    maxRetries: POLLING_CONFIG.maxRetries
  })
  const [nextStatus, setNextStatus] = useState<EnumEstadoCiclo | null>(null)

  const previousStatus = useRef<EnumEstadoCiclo | null>(null)
  const statusConfig = invoiceCycleStatusConfig[currentStatus]

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async () => {
      try {
        const newStatus = await fetchCycleStatus()

        // Navegar si:
        // - No está en Procesando
        // - Es diferente al originState (evita race conditions);
        //   excepción: si ya pasamos por Procesando, volver al originState es una finalización legítima
        // - Y (es primer fetch O acabamos de salir de Procesando)
        const hasTransitionedThroughProcessing =
          previousStatus.current === EnumEstadoCiclo.Procesando
        if (
          newStatus !== EnumEstadoCiclo.Procesando &&
          (!originState || newStatus !== originState || hasTransitionedThroughProcessing)
        ) {
          if (
            previousStatus.current === null ||
            previousStatus.current === EnumEstadoCiclo.Procesando
          ) {
            // en cada fetch guardamos el estado anterior. Si el estado anterior es != procesando, entonces prevenimos detener el timer nuevamente.
            stopRetriesTimer()
            handleStatusChange(newStatus)
          }
        }

        previousStatus.current = newStatus
      } catch (error) {
        console.error('Error fetching invoice cycle status:', error)
        throw error
      }
    },
    POLLING_CONFIG.delay,
    {
      immediate: false,
      retries: POLLING_CONFIG.maxRetries,
      onRetry: count => {
        setPollingState(prev => ({
          ...prev,
          isActive: true,
          currentRetry: count,
          isPaused: false
        }))
      },
      onFinishRetries: () => {
        // Se alcanzó el máximo de intentos sin cambio de estado
        setPollingState(prev => ({
          ...prev,
          isActive: false,
          isPaused: true
        }))
      },
      onError: error => {
        // Error en el polling - pausar automáticamente
        setPollingState(prev => ({
          ...prev,
          isActive: false,
          isPaused: true
        }))
      }
    }
  )

  /**
   * Maneja el cambio de estado y navega según corresponda
   */
  const handleStatusChange = (newStatus: EnumEstadoCiclo) => {
    setNextStatus(newStatus)

    if (!disableAutoNavigation) {
      navigateToStatus(newStatus)
    }
  }

  /**
   * Navega al estado correspondiente
   */
  const navigateToStatus = (status: EnumEstadoCiclo) => {
    const config = invoiceCycleStatusConfig[status]
    const route = resolveNavigateTo(config.navigateTo, additionalNavigationState)

    if (route) {
      history.push(route, {
        executionCycleId,
        ...additionalNavigationState
      })
    } else {
      // Estado no contemplado - volver al listado
      history.push(NonAuthRoutes.invoiceCyclesNew)
    }
  }

  /**
   * Navega manualmente al siguiente estado
   */
  const navigateToNext = () => {
    if (nextStatus) {
      navigateToStatus(nextStatus)
    }
  }

  /**
   * Reanuda el polling después de una pausa
   * Hace una comprobación inmediata y luego inicia el polling normal
   */
  const resumePolling = async () => {
    const awaitingStart = originState !== undefined && currentStatus === originState
    const shouldResume = statusConfig.isProcessing || awaitingStart
    if (pollingState.isPaused && shouldResume) {
      // Llamada inmediata al reanudar manualmente
      try {
        const newStatus = await fetchCycleStatus()

        const hasTransitionedResume = previousStatus.current === EnumEstadoCiclo.Procesando
        if (
          newStatus !== EnumEstadoCiclo.Procesando &&
          (!originState || newStatus !== originState || hasTransitionedResume)
        ) {
          // Estado cambió a uno válido (no race condition), navegar
          handleStatusChange(newStatus)
          previousStatus.current = newStatus
        } else {
          // Sigue procesando o es el estado inicial, iniciar polling normal
          previousStatus.current = newStatus
          startRetriesTimer()
        }
      } catch (error) {
        console.error('Error in resume:', error)
        // Aunque falle, iniciar el polling normal
        startRetriesTimer()
      }
    }
  }

  /**
   * Cancela el polling
   */
  const cancelPolling = () => {
    stopRetriesTimer()
    previousStatus.current = null
    setPollingState({
      isActive: false,
      currentRetry: 0,
      isPaused: true,
      maxRetries: POLLING_CONFIG.maxRetries
    })
  }

  /**
   * Inicia el polling automáticamente:
   * - Si el estado actual es "Procesando"
   * - O si venimos de un originState y el BE todavía no arrancó (currentStatus === originState)
   */
  useEffect(() => {
    const awaitingStart = originState !== undefined && currentStatus === originState
    const shouldPoll = statusConfig.isProcessing || awaitingStart
    if (shouldPoll && !pollingState.isActive && !pollingState.isPaused) {
      startRetriesTimer()
    }

    // Cleanup al desmontar o cambiar de estado
    return () => {
      if (isRetriesTimerActive()) {
        stopRetriesTimer()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusConfig.isProcessing, currentStatus])

  /**
   * Detecta transiciones de estado durante la sesión actual
   * (comparando el estado actual con el render anterior)
   *
   * IMPORTANTE: Solo navega después del primer fetch del polling.
   * Usa originState (pasado desde la página de origen) para determinar
   * si realmente hubo un cambio de estado válido, evitando race conditions.
   */
  useEffect(() => {
    if (
      previousStatus.current !== currentStatus &&
      currentStatus !== EnumEstadoCiclo.Procesando &&
      previousStatus.current !== null &&
      (!originState || currentStatus !== originState)
    ) {
      handleStatusChange(currentStatus)
    }
    previousStatus.current = currentStatus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatus])

  return {
    pollingState,
    resumePolling,
    cancelPolling,
    nextStatus,
    navigateToNext
  }
}
